import { useMemo, useState } from "react";
import { Plus, X, Sparkles, ListChecks, ArrowRight, Loader2, Wallet } from "lucide-react";

const AISLE_ORDER = ["Produce", "Dairy & Eggs", "Bakery", "Meat & Seafood", "Frozen", "Pantry Staples", "Household & Health"];
const AISLE_EMOJI = {
  "Produce": "🥦", "Dairy & Eggs": "🧀", "Bakery": "🍞", "Pantry Staples": "🥫",
  "Meat & Seafood": "🥩", "Frozen": "🍦", "Household & Health": "🧼",
};

export const GroceryListBuilder = ({ items, products, onAdd, onRemove, onSeedDemo, seeding, onNext, voiceSlot, budget, onBudget }) => {
  const [q, setQ] = useState("");

  const suggestions = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return [];
    const have = new Set(items.map((i) => (i.product_id || i.name)));
    return products
      .filter((p) => !have.has(p.id) &&
        ((p.name || "").toLowerCase().includes(t) || (p.brand || "").toLowerCase().includes(t)))
      .slice(0, 6);
  }, [q, products, items]);

  // Group list rows under their canonical aisle sub-headers (instant, no lag).
  const grouped = useMemo(() => {
    const g = {};
    items.forEach((it, i) => {
      const a = it.aisle || "Pantry Staples";
      (g[a] = g[a] || []).push({ ...it, _i: i });
    });
    return AISLE_ORDER.filter((a) => g[a]).map((a) => [a, g[a]]);
  }, [items]);

  const addFreeText = () => {
    const name = q.trim();
    if (!name) return;
    const match = products.find((p) => (p.name || "").toLowerCase() === name.toLowerCase());
    onAdd({ name: match ? match.name : name, product_id: match ? match.id : null, aisle: match ? match.aisle : undefined });
    setQ("");
  };

  return (
    <div className="flex flex-col gap-4" data-testid="sns-builder">
      <div>
        <h2 className="text-2xl font-black text-[#2B2A26]">Your shopping list</h2>
        <p className="text-sm text-black/50 mt-0.5">Hold the mic and talk, or type — we'll find who's cheapest.</p>
      </div>

      {voiceSlot}

      {/* Weekly budget — drives the budget status bar + email summary */}
      <div className="rounded-2xl bg-white border border-black/5 px-4 py-3.5 shadow-[0_2px_10px_rgba(0,0,0,0.03)]" data-testid="sns-budget-card">
        <label className="flex items-center gap-2 text-[13px] font-black uppercase tracking-wide text-[#2E7D4F]">
          <Wallet className="h-4 w-4" /> My Weekly Grocery Budget
        </label>
        <div className="mt-2 flex items-center gap-2">
          <span className="text-lg font-black text-[#2B2A26]">$</span>
          <input
            type="number"
            inputMode="decimal"
            min="0"
            step="1"
            value={budget}
            onChange={(e) => onBudget(e.target.value)}
            placeholder="e.g. 500"
            data-testid="sns-budget-input"
            className="flex-1 rounded-xl border-2 border-black/10 bg-[#FBF7EF] px-3 py-2.5 text-base outline-none focus:border-[#2E7D4F] transition-colors"
          />
        </div>
        <p className="mt-1.5 text-[11px] text-black/45">We'll flag if your optimised cart goes over the cap.</p>
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 text-black/30 text-[11px] font-bold uppercase tracking-wider mb-1">
          <span className="h-px flex-1 bg-black/10" /> or type it <span className="h-px flex-1 bg-black/10" />
        </div>
        <div className="flex gap-2">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addFreeText()}
            placeholder="e.g. milk, bread, eggs…"
            data-testid="sns-item-input"
            className="flex-1 rounded-2xl border-2 border-black/10 bg-white px-4 py-3.5 text-base outline-none focus:border-[#2E7D4F] transition-colors"
          />
          <button
            onClick={addFreeText}
            data-testid="sns-add-btn"
            className="h-[52px] w-[52px] shrink-0 grid place-items-center rounded-2xl bg-[#2E7D4F] text-white active:scale-95 transition-transform"
            aria-label="Add item"
          >
            <Plus className="h-5 w-5" />
          </button>
        </div>
        {suggestions.length > 0 && (
          <div className="absolute z-10 mt-2 w-full rounded-2xl bg-white border border-black/10 shadow-xl overflow-hidden">
            {suggestions.map((s, i) => (
              <button
                key={s.id}
                data-testid={`sns-suggestion-${i}`}
                onClick={() => { onAdd({ name: s.name, product_id: s.id, aisle: s.aisle }); setQ(""); }}
                className="w-full text-left px-4 py-3 hover:bg-[#2E7D4F]/5 border-b border-black/5 last:border-0 flex items-center justify-between"
              >
                <span className="text-sm font-medium text-[#2B2A26]">{s.name}</span>
                {s.brand && <span className="text-[11px] text-black/40">{s.brand}</span>}
              </button>
            ))}
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className="rounded-3xl border-2 border-dashed border-black/10 bg-white/50 px-5 py-8 text-center">
          <ListChecks className="h-8 w-8 mx-auto text-black/20" />
          <p className="mt-2 text-sm text-black/45">Your list is empty.</p>
          <button
            onClick={onSeedDemo}
            disabled={seeding}
            data-testid="sns-seed-demo"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#2E7D4F]/10 text-[#2E7D4F] font-semibold text-sm px-4 py-2.5 hover:bg-[#2E7D4F]/15 transition-colors disabled:opacity-50"
          >
            {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            Load demo specials
          </button>
        </div>
      ) : (
        <div className="space-y-4" data-testid="sns-grouped-list">
          {grouped.map(([aisle, rows]) => (
            <div key={aisle} data-testid={`sns-aisle-${aisle.replace(/[^a-z]/gi, "-").toLowerCase()}`}>
              <h3 className="flex items-center gap-2 text-[13px] font-black uppercase tracking-wide text-[#2E7D4F] mb-1.5">
                <span className="text-base">{AISLE_EMOJI[aisle]}</span>{aisle}
                <span className="text-black/30 font-semibold">· {rows.length}</span>
              </h3>
              <ul className="space-y-2">
                {rows.map((it) => (
                  <li
                    key={it.key}
                    data-testid={`sns-item-${it._i}`}
                    className="flex items-center justify-between rounded-2xl bg-white border border-black/5 px-4 py-3 shadow-[0_2px_10px_rgba(0,0,0,0.03)]"
                  >
                    <span className="text-[15px] font-medium text-[#2B2A26] flex items-center gap-2">
                      {it.name}
                      {!it.product_id && (
                        <span className="text-[10px] uppercase tracking-wide text-amber-600 bg-amber-50 rounded px-1.5 py-0.5">
                          not tracked
                        </span>
                      )}
                    </span>
                    <button
                      onClick={() => onRemove(it.key)}
                      data-testid={`sns-item-remove-${it._i}`}
                      className="h-8 w-8 grid place-items-center rounded-full text-black/30 hover:text-[#E4572E] hover:bg-[#E4572E]/10 transition-colors"
                      aria-label="Remove"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}

      {items.length > 0 && (
        <button
          onClick={onNext}
          data-testid="sns-to-stores"
          className="mt-1 w-full rounded-2xl bg-[#2B2A26] text-white font-bold py-4 text-base active:scale-[0.99] transition-transform flex items-center justify-center gap-2"
        >
          Pick my stores <ArrowRight className="h-5 w-5" />
        </button>
      )}
    </div>
  );
};
